import React, { useEffect, useState, useRef } from "react";
import CreateTaskForm from "./CreateTaskForm";
import { FaTrash, FaEdit, FaEye, FaFilter, FaPlus } from "react-icons/fa";
import "./Task.scss";

interface Task {
  id: number;
  title: string;
  description: string;
  due_date?: string;
  reminder_date?: string;
  reminder_time?: string;
  reminder_frequency?: string;
  status: string;
  tags?: string[];
  notes?: string;
  created_at: string;
}

const TaskList: React.FC = () => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [expandedTask, setExpandedTask] = useState<Task | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [showFilter, setShowFilter] = useState(false);
  const [filter, setFilter] = useState({
    status: "",
    date: "",
    tags: [] as string[],
  });
  const [tags, setTags] = useState<string[]>([]);
  const filterRef = useRef<HTMLDivElement>(null);

  
  useEffect(() => {
    const storedTasks = JSON.parse(localStorage.getItem("tasks") || "[]");
    const storedTags = JSON.parse(localStorage.getItem("tags") || "[]");
    setTasks(storedTasks);
    setTags(storedTags);
  }, []);

  
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) {
        setShowFilter(false);
      }
    };
    if (showFilter) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showFilter]);

  
  const saveTasks = (updated: Task[]) => {
    setTasks(updated);
    localStorage.setItem("tasks", JSON.stringify(updated));
  };

  
  const handleTaskCreated = (updatedTask: Task) => {
    let updatedTasks: Task[];
    if (editingTask) {
      updatedTasks = tasks.map((t) => (t.id === updatedTask.id ? updatedTask : t));
    } else {
      updatedTasks = [...tasks, updatedTask];
    }
    saveTasks(updatedTasks);
    setShowForm(false);
    setEditingTask(null);
  };

  
  const handleDelete = (id: number) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this task?");
    if (!confirmDelete) return;
    const updated = tasks.filter((t) => t.id !== id);
    saveTasks(updated);
  };

  
  const toggleComplete = (id: number) => {
    const updated = tasks.map((t) =>
      t.id === id ? { ...t, status: t.status === "completed" ? "pending" : "completed" } : t
    );
    saveTasks(updated);
  };

  
  const toggleTagFilter = (tag: string) => {
    setFilter((prev) => {
      const exists = prev.tags.includes(tag);
      const newTags = exists ? prev.tags.filter((t) => t !== tag) : [...prev.tags, tag];
      return { ...prev, tags: newTags };
    });
  };

  
  const filteredTasks = tasks.filter((t) => {
    if (filter.status && t.status !== filter.status) return false;

    if (filter.date) {
      const today = new Date();
      const due = t.due_date ? new Date(t.due_date) : null;
      if (due) {
        if (filter.date === "overdue" && !(due < today)) return false;
        if (filter.date === "week") {
          const weekEnd = new Date();
          weekEnd.setDate(today.getDate() + 7);
          if (!(due >= today && due <= weekEnd)) return false;
        }
        if (filter.date === "month") {
          if (!(due.getMonth() === today.getMonth() && due.getFullYear() === today.getFullYear()))
            return false;
        }
      } else if (filter.date !== "") return false;
    }

    if (filter.tags.length > 0) {
      const taskTags = t.tags ?? [];
      if (!filter.tags.every((ft) => taskTags.includes(ft))) return false;
    }

    return true;
  });

  return (
    <div className="task-container">
      <div className="task-header">
        <h1>My Tasks</h1>
        <div className="header-actions" ref={filterRef}>
          <button
            className="filter-btn"
            onClick={() => setShowFilter((prev) => !prev)}
          >
            <FaFilter /> Filter
          </button>
          <button className="create-btn" onClick={() => setShowForm(true)}>
            <FaPlus /> New Task
          </button>

          {showFilter && (
            <div className="filter-menu">
              <label>Status</label>
              <select
                value={filter.status}
                onChange={(e) => setFilter({ ...filter, status: e.target.value })}
              >
                <option value="">All</option>
                <option value="pending">Pending</option>
                <option value="completed">Completed</option>
              </select>

              <label>Due Date</label>
              <select
                value={filter.date}
                onChange={(e) => setFilter({ ...filter, date: e.target.value })}
              >
                <option value="">All</option>
                <option value="overdue">Overdue</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
              </select>

              <label>Tags</label>
              <div className="filter-tags">
                {tags.map((tag) => (
                  <label key={tag} className="filter-tag">
                    <input
                      type="checkbox"
                      checked={filter.tags.includes(tag)}
                      onChange={() => toggleTagFilter(tag)}
                    />
                    {"  "}{tag} {"       "}
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <table className="task-table">
        <thead>
          <tr>
            <th>Title</th>
            <th>Due Date</th>
            <th>Status</th>
            <th>Tags</th>
            <th className="actions-header"></th>
          </tr>
        </thead>
        <tbody>
          {filteredTasks.length > 0 ? (
            filteredTasks.map((t) => (
              <tr key={t.id}>
                <td>{t.title}</td>
                <td>{t.due_date || "—"}</td>
                <td className="status-cell">
                  <label className="status-label">
                    <input
                      type="checkbox"
                      checked={t.status === "completed"}
                      onChange={() => toggleComplete(t.id)}
                    />
                    <span>{t.status}</span>
                  </label>
                </td>
                <td>{t.tags?.join(", ") || "—"}</td>
                <td className="actions right-align">
                  <button onClick={() => setExpandedTask(t)}><FaEye /></button>
                  <button onClick={() => { setEditingTask(t); setShowForm(true); }}><FaEdit /></button>
                  <button onClick={() => handleDelete(t.id)}><FaTrash /></button>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={5} className="no-tasks">No tasks found</td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="bottom-create">
        <button onClick={() => setShowForm(true)} className="create-btn">
          <FaPlus /> Create Task
        </button>
      </div>

      {expandedTask && (
        <div className="popup-overlay" onClick={() => setExpandedTask(null)}>
          <div className="popup-view" onClick={(e) => e.stopPropagation()}>
            <h2>{expandedTask.title}</h2>
            <div className="detail-row"><strong>Description:</strong> <span>{expandedTask.description || "No description"}</span></div>
            <div className="detail-row"><strong>Notes:</strong> <span>{expandedTask.notes || "None"}</span></div>
            <div className="detail-row"><strong>Due Date:</strong> <span>{expandedTask.due_date || "—"}</span></div>
            <div className="detail-row"><strong>Status:</strong> <span>{expandedTask.status}</span></div>
            <div className="detail-row"><strong>Tags:</strong> <span>{expandedTask.tags?.join(", ") || "—"}</span></div>

            <div className="details-actions">
              <button onClick={() => setExpandedTask(null)}>Close</button>
              <button onClick={() => { setEditingTask(expandedTask); setShowForm(true); setExpandedTask(null); }}>Edit</button>
              <button onClick={() => handleDelete(expandedTask.id)}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <div className="popup-overlay">
          <div className="popup-form">
            <CreateTaskForm onTaskCreated={handleTaskCreated} editingTask={editingTask} />
            <button className="close-btn" onClick={() => { setShowForm(false); setEditingTask(null); }}>✕</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskList;
